//import * as Sentry from '@sentry/react';
//import { Integrations } from '@sentry/tracing';
import { notification } from 'antd';
import { ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import axios, { AxiosError } from 'axios';
import { isEmpty } from 'lodash';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';

import App from './App';
import AuthProvider from './contexts/authContext';
import * as cognito from './library/cognito';
import reportWebVitals from './reportWebVitals';

let refreshPromise: Promise<cognito.UserSession> | null = null;

/*
Sentry.init({
  dsn: 'https://c6f74300ad8b4eb3946095b6cfd23fcf@o766229.ingest.sentry.io/5797649',
  integrations: [new Integrations.BrowserTracing()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});
*/

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// auth interceptor first so it receives the full Axios error with config
axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    const headers = (originalRequest as any)?.headers || {};
    const hadAuthToken = Boolean(
      headers.Authorization || headers.authorization
    );

    // A 401/403 with no token is an unauthenticated request (for example a
    // failed login), not an expired session.
    if (
      hadAuthToken &&
      (status === 401 || status === 403) &&
      originalRequest &&
      !(originalRequest as any)._retry
    ) {
      try {
        if (!refreshPromise) {
          refreshPromise = cognito.refreshSession();
        }
        const newSession = await refreshPromise;
        refreshPromise = null;

        axios.defaults.headers.common['Authorization'] =
          newSession.tokens.IdToken;
        (originalRequest as any).headers =
          (originalRequest as any).headers || {};
        (originalRequest as any).headers['Authorization'] =
          newSession.tokens.IdToken;
        (originalRequest as any)._retry = true;

        return axios(originalRequest);
      } catch (refreshErr) {
        refreshPromise = null;
        notification.warning({
          description: 'Your session has expired. Please log in again.',
          duration: 0,
          message: 'Session expired',
        });
      }
    }
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<Error>) => {
    if (isEmpty(error.response?.data)) {
      return Promise.reject(new Error('Server Error'));
    }
    const errorObj = error.response?.data as any;
    if (errorObj.errors?.[0]?.message) {
      return Promise.reject(new Error(errorObj.errors?.[0]?.message));
    }
    return Promise.reject(error.response?.data);
  }
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // keep cached data fresh so navigating back shows content immediately (like View Rates)
      cacheTime: 10 * 60 * 1000, // 10mins
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2mins
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <QueryClientProvider client={queryClient}>
    <ConfigProvider locale={enUS}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ConfigProvider>
  </QueryClientProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// swap favicon based on domain
let favImg = '/favicon.ico';
let pageTitle = 'MVS Canada - Carrier Portal';
if (window.location.host === 'www.carshippingrates.ca') {
  favImg = '/faviconcsr.ico';
  pageTitle = 'CarShippingRates.ca';
}

let headTitle = document.querySelector('head');
let setFavicon = document.createElement('link');
setFavicon.setAttribute('rel', 'shortcut icon');
setFavicon.setAttribute('href', favImg);
headTitle?.appendChild(setFavicon);

document.title = pageTitle;
